import { motion } from "framer-motion";
import { Globe, Smartphone, MoonStar, Compass, BookOpen, Sparkles } from "lucide-react";
import { useContent } from "@/context/ContentContext";

const ICONS = [Globe, Smartphone, MoonStar, Compass, BookOpen];

const cardBase =
  "group relative bg-white border border-[#122A1D]/10 hover:border-[#C8A24A]/60 shadow-[0_8px_30px_rgba(18,42,29,0.06)] transition-all duration-500 hover:-translate-y-1 p-8 overflow-hidden";

const Glow = () => (
  <div className="absolute -top-20 -right-20 w-48 h-48 rounded-full bg-[#C8A24A]/0 group-hover:bg-[#C8A24A]/10 blur-3xl transition-all duration-700 pointer-events-none" />
);

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } },
};

const DEFAULT_CARDS = [
  { title: "Web Application Development", desc: "Bespoke platforms, portals, and progressive web apps — engineered with modern stacks and designed with the elegance of Islamic geometry.", tags: ["React", "FastAPI", "PWA", "E-commerce", "Portals"] },
  { title: "Mobile App Development", desc: "Native-quality iOS and Android experiences — prayer companions, Qibla finders, Quran readers, and community apps that feel effortless in the hand.", tags: [] },
  { title: "Islamic Domain Expertise", desc: "Hijri calendars, salah times, zakat calculators, halal directories — built with deep understanding of the deen.", tags: [] },
  { title: "Interactive Design", desc: "Highly visual, motion-rich interfaces inspired by sacred geometry and the tranquility of nature.", tags: [] },
  { title: "Knowledge Platforms", desc: "E-learning systems, digital libraries, and dawah tools that make authentic knowledge accessible to all.", tags: [] },
];

const DEFAULT_STATS = [
  { value: "Web + Mobile", label: "Full-stack delivery" },
  { value: "786", label: "Bismillah in every build" },
  { value: "Ummah-first", label: "Community focused" },
  { value: "Polished", label: "Pixel-perfect craft" },
];

const SMALL_CARD_TESTIDS = ["service-card-islamic", "service-card-design", "service-card-content"];

export const Services = () => {
  const { content } = useContent();
  const s = content?.services;
  const cards = s?.cards || DEFAULT_CARDS;
  const stats = s?.stats || DEFAULT_STATS;

  const c0 = cards[0] || DEFAULT_CARDS[0];
  const c1 = cards[1] || DEFAULT_CARDS[1];
  const smallCards = (cards.slice(2, 5).length ? cards.slice(2, 5) : DEFAULT_CARDS.slice(2, 5));

  return (
    <section id="services" data-testid="services-section" className="relative py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          variants={fadeUp}
          className="mb-16 max-w-2xl"
        >
          <p className="uppercase tracking-[0.35em] text-xs text-[#A9842F] mb-6 flex items-center gap-4">
            <span className="w-12 h-px bg-[#C8A24A]/60" />
            {s?.eyebrow || "What we craft"}
          </p>
          <h2 data-testid="services-heading" className="font-heading text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-[#122A1D]">
            {s?.heading || "Capabilities rooted in"}{" "}
            <em className="text-[#A9842F]">{s?.heading_accent || "purpose"}</em>
          </h2>
        </motion.div>

        {/* Bento grid */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-60px" }}
          transition={{ staggerChildren: 0.12 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-5"
        >
          {/* Large card */}
          <motion.div variants={fadeUp} data-testid="service-card-web" className={`${cardBase} md:col-span-2 md:row-span-2 flex flex-col justify-between min-h-[320px]`}>
            <Glow />
            <Globe className="text-[#A9842F]" size={36} strokeWidth={1.2} />
            <div>
              <h3 className="font-heading text-3xl lg:text-4xl text-[#122A1D] mb-4">{c0.title}</h3>
              <p className="text-[#5B6F64] font-light leading-relaxed max-w-lg">{c0.desc}</p>
              {c0.tags?.length > 0 && (
                <div className="mt-6 flex flex-wrap gap-2">
                  {c0.tags.map((t) => (
                    <span key={t} className="text-xs uppercase tracking-wider border border-[#122A1D]/15 text-[#5B6F64] px-3 py-1">{t}</span>
                  ))}
                </div>
              )}
            </div>
          </motion.div>

          {/* Tall card */}
          <motion.div variants={fadeUp} data-testid="service-card-mobile" className={`${cardBase} md:row-span-2 flex flex-col justify-between min-h-[320px]`}>
            <Glow />
            <Smartphone className="text-[#A9842F]" size={36} strokeWidth={1.2} />
            <div>
              <h3 className="font-heading text-3xl text-[#122A1D] mb-4">{c1.title}</h3>
              <p className="text-[#5B6F64] font-light leading-relaxed">{c1.desc}</p>
            </div>
          </motion.div>

          {/* Small cards */}
          {smallCards.map((card, i) => {
            const Icon = ICONS[i + 2] || BookOpen;
            return (
              <motion.div key={i} variants={fadeUp} data-testid={SMALL_CARD_TESTIDS[i]} className={cardBase}>
                <Glow />
                <Icon className="text-[#A9842F] mb-5" size={30} strokeWidth={1.2} />
                <h3 className="font-heading text-2xl text-[#122A1D] mb-3">{card.title}</h3>
                <p className="text-[#5B6F64] font-light text-sm leading-relaxed">{card.desc}</p>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Stats ribbon */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
          data-testid="services-stats"
          className="mt-16 border-y border-[#C8A24A]/20 py-10 grid grid-cols-2 md:grid-cols-4 gap-8"
        >
          {stats.map((st, i) => (
            <div key={st.label || i} className="text-left">
              <p className="font-heading text-2xl lg:text-3xl text-[#A9842F]">
                {i === 0 && <Sparkles className="inline mr-1 mb-1" size={18} />}
                {st.value}
              </p>
              <p className="text-xs uppercase tracking-[0.2em] text-[#5B6F64] mt-2">{st.label}</p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
